import {
	Box,
	FormControl,
	FormLabel,
	Input,
	Button,
	Stack,
	Text,
	HStack,
	chakra,
	Flex,
	useBreakpointValue,
	Link,
	RangeSlider,
	RangeSliderTrack,
	RangeSliderFilledTrack,
	RangeSliderThumb,
	Spinner,
	FormErrorMessage,
	useDisclosure,
	InputGroup,
	InputLeftElement,
} from '@chakra-ui/react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import api from '../../utils/axios';
import { useEffect, useState } from 'react';
import { formatNumber } from '../../utils/helper';
import { MdGraphicEq } from 'react-icons/md'; // Import icon for RangeSliderThumb
import MintInfoModal from '../checkRank/MintInfoModal';
import { useConnectModal } from '@rainbow-me/rainbowkit';
import { useAccount } from 'wagmi';
import { Link as RouterLink } from 'react-router-dom';

// Validation Schema
const validationSchema = Yup.object().shape({
	marksFrom: Yup.number().nullable().min(0, 'Marks from cannot be negative'),
	marksTo: Yup.number()
		.nullable()
		.when('marksFrom', (marksFrom, schema) => {
			return marksFrom ? schema.min(marksFrom, 'Marks to should be greater than or equal to Marks from') : schema;
		}),
	badgesFrom: Yup.number().nullable().min(0, 'Badges from cannot be negative'),
	badgesTo: Yup.number()
		.nullable()
		.when('badgesFrom', (badgesFrom, schema) => {
			return badgesFrom ? schema.min(badgesFrom, 'Badges to should be greater than or equal to Badges from') : schema;
		}),
	address: Yup.string().required('Address is required')
});

// Function to send data to the backend
const getCustomRank = async (address, marksFrom, marksTo, badgesFrom, badgesTo, userAddress) => {
	try {
		const userSignature = localStorage.getItem('userSignature');
		const response = await api.post('/check-custom-rank', { address, marksFrom, marksTo, badgesFrom, badgesTo, user: userAddress, signature: userSignature });
		return response.data;
	} catch (e) {
		console.log('Error:', e);
		throw e;
	}
};

const getRangeInfo = async () => {
	try {
		const response = await api.get('/range-info');
		return response.data;
	} catch (e) {
		console.log('Error:', e);
		throw e;
	}
};

const CustomRankForm = ({ isConnected, isUserHasKey }) => {
	const [responseInfo, setResponseInfo] = useState(null);
	const [loading, setLoading] = useState(true);
	const [marksRange, setMarksRange] = useState([0, '']);
	const [badgesRange, setBadgesRange] = useState([0, '']);
	const [maxMarks, setMaxMarks] = useState();
	const [maxBadges, setMaxBadges] = useState();
	const isMobile = useBreakpointValue({ base: true, md: false });
	const { isOpen: isMintModalOpen, onOpen: onMintModalOpen, onClose: onMintModalClose } = useDisclosure();
	const { openConnectModal } = useConnectModal();
	const { address: userAddress } = useAccount();

	useEffect(() => {
		const fetchRangeInfo = async () => {
			try {
				const data = await getRangeInfo();
				// setMaxMarks(Math.min(data.maxMarks, 10000)); // Ensure maxMarks does not exceed 10000
				setMaxMarks(data.maxMarks); // Ensure maxMarks does not exceed 10000
				setMaxBadges(data.maxCountBadges);
				// setMarksRange([0, Math.min(data.maxMarks, 10000)]);
				setMarksRange([0, data.maxMarks]);
				setBadgesRange([0, data.maxCountBadges]);
			} catch (error) {
				console.error('Error fetching range info', error);
			} finally {
				setLoading(false);
			}
		};
		fetchRangeInfo();
	}, []);

	const handleSubmit = async (values) => {
		// setLoading(true);
		const { address } = values;
		try {
			const data = await getCustomRank(address, marksRange[0], marksRange[1], badgesRange[0], badgesRange[1], userAddress);
			setResponseInfo(data);
		} catch (error) {
			console.error('Error fetching data', error);
		} finally {
			// setLoading(false);
		}
	};

	return (
		<Box>
			{responseInfo && (
				<Box pb={4}>
					{(responseInfo.totalMarks < responseInfo.marksRange.marks_from || responseInfo.totalMarks > responseInfo.marksRange.marks_to ||
						responseInfo.totalBadges < responseInfo.badgesRange.badges_from || responseInfo.totalBadges > responseInfo.badgesRange.badges_to) ? (
						<Box mt={6} p={4} borderRadius="md" borderColor="red" border="1px solid">
							<Text>
								{(responseInfo.totalMarks < responseInfo.marksRange.marks_from || responseInfo.totalMarks > responseInfo.marksRange.marks_to) &&
									<span>
										Your wallet has
										<chakra.span color="#FF5733" fontWeight="bold"> {parseFloat(responseInfo.totalMarks).toFixed(2)} </chakra.span>
										marks, which does not fit the filter <chakra.span fontWeight="bold">"Marks from {responseInfo.marksRange.marks_from} to {responseInfo.marksRange.marks_to}</chakra.span>".
									</span>
								}
								{(responseInfo.totalBadges < responseInfo.badgesRange.badges_from || responseInfo.totalBadges > responseInfo.badgesRange.badges_to) &&
									<span>
										Your wallet has
										<chakra.span color="#FF5733" fontWeight="bold"> {parseInt(responseInfo.totalBadges)} </chakra.span>
										badges, which does not fit the filter <chakra.span fontWeight="bold">"Badges from {responseInfo.badgesRange.badges_from} to {responseInfo.badgesRange.badges_to}</chakra.span>".
									</span>
								}
							</Text>
						</Box>
					) :
						<>
							<Box mt={2} p={4} borderColor="gray.200" background={"#FEF0DD"} borderWidth={0} borderRadius="lg" mb={4}>
								{/* <Flex color={"#FF684B"} fontWeight="bold" justifyContent={"center"} fontSize={{ base: "2xl", md: "4xl" }}>
									#{formatNumber(responseInfo.rank)}
								</Flex> */}
								<Text fontSize={{ base: "4xl", md: "7xl" }} fontWeight="bold" color="#FF684B">
									#{formatNumber(responseInfo.rank)}
								</Text>
								<Box pb={1} fontSize={"sm"} textAlign={"center"}>
									{" "}<chakra.span color={"#FF684B"}>{parseFloat(responseInfo.totalMarks).toFixed(2)}</chakra.span>{" "} marks
									and <chakra.span color={"#FF684B"}>{responseInfo.totalBadges}</chakra.span> badges.
								</Box>
								<Text p={4} textAlign={"center"}>
									Wallet <b>{responseInfo.address}</b> is in top
									<chakra.span color={"#FF684B"} fontWeight="bold"> {responseInfo.rankPercentage}% </chakra.span>
									and better than {formatNumber(responseInfo.totalUsersInRange - responseInfo.rank)}{' '}
									wallets of {formatNumber(responseInfo.totalUsersInRange)}.<br /> This top rank is among those with marks from {responseInfo.marksRange.marks_from} to
									{' '}{responseInfo.marksRange.marks_to} and badges from
									{' '}{responseInfo.badgesRange.badges_from} to
									{' '}{responseInfo.badgesRange.badges_to}.
								</Text>
							</Box>

						</>
					}
				</Box>
			)}
			<Formik
				initialValues={{
					address: ''
				}}
				validationSchema={validationSchema}
				onSubmit={handleSubmit}
			>
				{({ errors, touched, values, isSubmitting }) => (
					<Form>
						<Stack spacing={4}>
							{/* Marks Range Slider */}
							<FormControl isInvalid={errors.address && touched.address}>
								<Field
									as={Input}
									fontSize={!isMobile ? "1.25rem" : '0.8rem'}
									id={"address"}
									name={"address"}
									placeholder={"Enter your wallet..."}
									height={isMobile ? '35px' : '60px'}
									minWidth={isMobile ? 'auto' : '700px'}
									width="100%"
									border="1px solid rgb(255, 104, 75)"
									outline="1px solid rgb(255, 104, 75)"
									textAlign="center"
									_focus={{
										borderColor: "rgb(255, 104, 75)",
										outline: "1px solid rgb(255, 104, 75)"
									}}
									sx={{
										'::placeholder': {
											fontSize: !isMobile ? "1.2rem" : '0.8rem',
											color: 'gray.400',
										}
									}}
								/>

								<FormErrorMessage>{errors.address}</FormErrorMessage>
							</FormControl>

							<FormControl>
								<Flex flexFlow={"row"} gap={4} mb={4} mt={2}>
									<Flex flex="1" justifyContent={"center"} alignItems={"center"}>
										<FormControl>
											<FormLabel htmlFor="marksFromInput">Marks From</FormLabel>
											<Field
												as={Input}
												id="marksFromInput"
												name="marksFromInput"
												type="number"
												value={marksRange[0]}
												onChange={(e) => {
													const newValue = parseFloat(e.target.value);
													setMarksRange([newValue, marksRange[1]]);
													// if (newValue >= 0 && newValue <= marksRange[1]) {
													// 	setMarksRange([newValue, marksRange[1]]);
													// }
												}}
												height={isMobile ? '35px' : '60px'}
												width="100%"
												border="1px solid rgb(255, 104, 75)"
												outline="1px solid rgb(255, 104, 75)"
												_focus={{
													borderColor: "rgb(255, 104, 75)",
													outline: "1px solid rgb(255, 104, 75)"
												}}
												sx={{
													'::placeholder': {
														fontSize: !isMobile ? "1.2rem" : '0.8rem',
														color: 'gray.400',
													}
												}}
											/>
										</FormControl>
									</Flex>
									<Flex flex="1" justifyContent={"center"} alignItems={"center"}>
										<FormControl>
											<FormLabel htmlFor="marksToInput">Marks To</FormLabel>
											<InputGroup>

												{loading ? <InputLeftElement><Spinner size="sm" color="orange" mt={4} /></InputLeftElement> : null}

												<Field
													as={Input}
													id="marksToInput"
													name="marksToInput"
													type="number"
													value={marksRange[1]}
													onChange={(e) => {
														const newValue = parseFloat(e.target.value);
														setMarksRange([marksRange[0], newValue]);
														// if (newValue >= marksRange[0] && newValue <= maxMarks) {
														// 	setMarksRange([marksRange[0], newValue]);
														// }
													}}
													height={isMobile ? '35px' : '60px'}
													width="100%"
													border="1px solid rgb(255, 104, 75)"
													outline="1px solid rgb(255, 104, 75)"
													_focus={{
														borderColor: "rgb(255, 104, 75)",
														outline: "1px solid rgb(255, 104, 75)"
													}}
													sx={{
														'::placeholder': {
															fontSize: !isMobile ? "1.2rem" : '0.8rem',
															color: 'gray.400',
														}
													}}
												/>
											</InputGroup>
										</FormControl>
									</Flex>
								</Flex>
							</FormControl>


							{/* Badges Range Slider */}
							<FormControl pb={2}>
								<FormLabel htmlFor="badgesRange">Badges Range</FormLabel>
								<RangeSlider
									aria-label={['min', 'max']}
									defaultValue={[0, maxBadges]}
									min={0}
									max={maxBadges}
									step={1}
									value={badgesRange}
									onChange={setBadgesRange}
									isDisabled={loading}
								>
									<RangeSliderTrack bg='red.100'>
										<RangeSliderFilledTrack bg='tomato' />
									</RangeSliderTrack>
									<RangeSliderThumb boxSize={6} index={0}>
										<Box color='tomato' as={MdGraphicEq} />
									</RangeSliderThumb>
									<RangeSliderThumb boxSize={6} index={1}>
										<Box color='tomato' as={MdGraphicEq} />
									</RangeSliderThumb>
								</RangeSlider>
								<HStack justifyContent="space-between">
									<Text>Badges from: {badgesRange[0]}</Text>
									{loading ? (
										<Box>
											<Spinner size="sm" color="orange" />
										</Box>
									) : (
										<Text>Badges to: {badgesRange[1]}</Text>
									)}
								</HStack>
								{/* Input fields for precise value */}
								{/* <HStack spacing={4}>
									<FormControl>
										<FormLabel htmlFor="badgesFromInput">Badges From</FormLabel>
										<Field
											as={Input}
											id="badgesFromInput"
											name="badgesFromInput"
											type="number"
											value={badgesRange[0]}
											onChange={(e) => {
												const newValue = parseFloat(e.target.value);
												if (newValue >= 0 && newValue <= badgesRange[1]) {
													setBadgesRange([newValue, badgesRange[1]]);
												}
											}}
										/>
									</FormControl>
									<FormControl>
										<FormLabel htmlFor="badgesToInput">Badges To</FormLabel>
										<Field
											as={Input}
											id="badgesToInput"
											name="badgesToInput"
											type="number"
											value={badgesRange[1]}
											onChange={(e) => {
												const newValue = parseFloat(e.target.value);
												if (newValue >= badgesRange[0] && newValue <= maxBadges) {
													setBadgesRange([badgesRange[0], newValue]);
												}
											}}
										/>
									</FormControl>
								</HStack> */}
							</FormControl>

							{/* <Text mb={2} fontSize="sm" color="gray.600">
								Use the sliders to select the range badges you want to include in your search.
							</Text> */}

							{!isConnected ?
								<Button
									type="button"
									background="#FF684B"
									color="white"
									_hover={{ background: "#FF5733" }}
									height={!isMobile ? "58px" : "35px"}
									// isDisabled={!isConnected}
									size={!isMobile ? 'lg' : 'xs'}
									// onClick={connect}
									onClick={openConnectModal}
								>
									GET RANK
								</Button> :
								<>
									{isUserHasKey ?
										<Button
											type="submit"
											isLoading={isSubmitting}
											background="#FF684B"
											color="white"
											_hover={{ background: "#FF5733" }}
											height={!isMobile ? "58px" : "35px"}
											isDisabled={!values.address}
											size={!isMobile ? 'lg' : 'xs'}
										>
											GET RANK
										</Button>
										:
										<Button
											type="button"
											background="#FF684B"
											color="white"
											_hover={{ background: "#FF5733" }}
											height={!isMobile ? "58px" : "35px"}
											// isDisabled={!isConnected}
											size={!isMobile ? 'lg' : 'xs'}
											// onClick={connect}
											onClick={onMintModalOpen}
										>
											GET RANK
										</Button>
									}
								</>
							}

						</Stack>
					</Form>
				)}
			</Formik>

			<MintInfoModal onOpen={onMintModalOpen} onClose={onMintModalClose} isOpen={isMintModalOpen} />

			{responseInfo ? <Text align="center" pt={4}>
				Want more marks?{' '}
				<Link to="/scroll-marks-calculator">
					<chakra.span color="#FF5733" _hover={{ textDecoration: "underline" }}>
						Try our Marks Calculator
					</chakra.span>
				</Link>
			</Text> :
				<Text align="center" pt={4}>
					Any question?{' '}
					<Link as={RouterLink} to="/faq">
						<chakra.span color="#FF5733" _hover={{ textDecoration: "underline" }}>
							Read FAQ
						</chakra.span>
					</Link>
				</Text>}
		</Box>
	);
};

export default CustomRankForm;
